import { motion, useReducedMotion } from "framer-motion"
import styled from "@emotion/styled"
import { fullGrid } from "css/grid"
import getMediaQuery from "css/breakpoints"
import Heading from "./Heading"
import Link from "components/link/Link"
import PopinButton from "components/popin-edito/PopinButton"
import VideoLauncher from "components/video-launcher/VideoLauncher"

const Root = styled(motion.div)`
  ${fullGrid}

  align-content: end;
  grid-area: 1/1;
  height: calc(100vh - var(--nav-main-safe-height));
  pointer-events: none;
  position: relative;

  &[data-isinteractive="true"] a,
  &[data-isinteractive="true"] button {
    pointer-events: auto;
  }
`

const Container = styled(motion.div)`
  align-items: center;
  display: flex;
  flex-direction: column;
  grid-column: main;
  grid-row: 1;
  padding-block-end: 5rem;
  row-gap: 1.25rem;
  position: relative;
  z-index: 0;

  ${getMediaQuery("m")} {
    grid-column: col 3 / span 8;
  }

  /* a11y */
  & hgroup {
    html.prefers-contrast & {
      color: black !important;
      position: relative;

      &::after {
        background: white;
        content: "";
        height: calc(100% + 20px);
        left: 50%;
        position: absolute;
        top: 50%;
        transform: translate(-50%, -50%);
        width: calc(100% + 20px);
        z-index: -1;
      }
    }
  }
`

const CTA = styled(motion.div)`
  display: flex;
  gap: 1rem;
`

const parentVariants = {
  show: {
    opacity: 1,
    y: 0,
    transition: {
      ease: "easeOut",
      duration: 0.6,
      when: "beforeChildren",
      staggerChildren: 0.18,
    },
  },
  pre: { opacity: 0, y: 36 },
  hide: {
    opacity: 0,
    y: 0,
    transition: {
      ease: "easeOut",
      duration: 0.6,
      when: "afterChildren",
      staggerChildren: 0.12,
      staggerDirection: -1,
    },
  },
}

const childVariants = {
  show: { opacity: 1, y: 0, transition: { ease: "easeOut", duration: 0.45 } },
  pre: { opacity: 0, y: 18 },
  hide: { opacity: 0, y: 0, transition: { ease: "easeOut", duration: 0.25 } },
}

function pickCtas(cta = []) {
  const picked = { link: null, videoPlayer: null, popin: null }

  for (const item of cta) {
    if (!picked.link && item?.link) picked.link = item.link
    if (!picked.videoPlayer && item?.video_player) picked.videoPlayer = item.video_player
    if (!picked.popin && item?.popin) picked.popin = item.popin
    if (picked.link && picked.videoPlayer && picked.popin) break
  }

  return picked
}

export default function TextBlock({ opacity = 1, isInteractive = true, heading, cta, use_os_logo, animated_logo, logo_translation }) {
  const reducedMotion = useReducedMotion()

  const animate = reducedMotion ? false : "show"
  const initial = reducedMotion ? false : "pre"

  const { link, videoPlayer, popin } = pickCtas(cta)
  const hasCtas = !!(link || videoPlayer || popin)

  return (
    <Root data-isinteractive={isInteractive.toString()} style={{ opacity }} className='textblock'>
      <Container variants={parentVariants} initial={initial} animate={animate}>
        <motion.div variants={childVariants}>
          <Heading {...heading} use_os_logo={use_os_logo} animated_logo={animated_logo} logo_translation={logo_translation} animate={animate || "show"} />
        </motion.div>

        {hasCtas && (
          <CTA variants={childVariants}>
            {link && <Link {...link} />}
            {videoPlayer && <VideoLauncher video_player_cld={videoPlayer} />}
            {popin && <PopinButton {...popin} />}
          </CTA>
        )}
      </Container>
    </Root>
  )
}
